import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Facebook, Youtube, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { scrollTo } from "@/lib/lenis";
import { LOGO } from "@/lib/site";

const LINKS = [
  { id: "about", label: "About Us" },
  { id: "events", label: "Events" },
  { id: "treasure-queen", label: "Treasure Queen" },
  { id: "treasure-king", label: "Treasure King" },
  { id: "gallery", label: "Gallery" },
  { id: "sponsors", label: "Sponsorship" },
  { id: "contact", label: "Contact" },
];

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: "HA Treasure House respects your privacy. Information shared through our registration and contact forms (such as name, contact details and photographs) is used solely to evaluate participation, communicate opportunities and organize our events. We do not sell or share your personal data with third parties without consent. You may request removal of your data at any time by contacting us at 8329031991.",
  },
  terms: {
    title: "Terms & Conditions",
    body: "Participation in HA Treasure House events is subject to registration acceptance and event-specific guidelines shared with shortlisted participants. Event dates, venues, categories and accolades may be updated at the discretion of the organizers. Photographs and videos from events may be used for promotional purposes. By registering, you confirm that the information provided is accurate.",
  },
};

export default function Footer() {
  const [policy, setPolicy] = useState(null);

  return (
    <footer className="bg-royal-900 border-t border-gold/20 pt-20 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 pb-14 border-b border-gold/10">
          <div>
            <div className="flex items-center gap-4">
              <img src={LOGO} alt="HA Treasure House logo" className="h-16 w-16 rounded-full object-cover ring-1 ring-gold/60" />
              <div>
                <p className="font-display text-xl gold-text font-semibold">HA TREASURE HOUSE</p>
                <p className="text-xs text-muted tracking-[0.2em] uppercase mt-1">Center of Excellence for Great Talent</p>
              </div>
            </div>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              <a href="https://www.instagram.com/HA_TreasureHouse" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram"
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-royal-950 transition-colors duration-300">
                <Instagram size={17} />
              </a>
              <a href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook"
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-royal-950 transition-colors duration-300">
                <Facebook size={17} />
              </a>
              <a href="https://www.youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube"
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-royal-950 transition-colors duration-300">
                <Youtube size={17} />
              </a>
              <a href="https://wa.me/918329031991" target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp"
                className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-royal-950 transition-colors duration-300">
                <FaWhatsapp size={17} />
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-5">Explore</p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {LINKS.map((l) => (
                <li key={l.id}>
                  <button onClick={() => scrollTo(`#${l.id}`)} className="text-sm text-muted hover:text-gold transition-colors" data-testid={`footer-link-${l.id}`}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-5">Legal</p>
            <ul className="space-y-3">
              <li>
                <button onClick={() => setPolicy("privacy")} className="text-sm text-muted hover:text-gold transition-colors" data-testid="footer-privacy-link">
                  Privacy Policy
                </button>
              </li>
              <li>
                <button onClick={() => setPolicy("terms")} className="text-sm text-muted hover:text-gold transition-colors" data-testid="footer-terms-link">
                  Terms & Conditions
                </button>
              </li>
              <li>
                <a href="/admin" className="text-sm text-muted hover:text-gold transition-colors" data-testid="footer-admin-link">
                  Admin Login
                </a>
              </li>
            </ul>
          </div>
        </div>

        <p className="pt-8 text-center text-xs text-muted/70 tracking-wider" data-testid="footer-copyright">
          © 2026 HA Treasure House. All Rights Reserved.
        </p>
      </div>

      <AnimatePresence>
        {policy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-royal-950/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setPolicy(null)}
            data-testid="policy-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              className="glass-card gold-frame rounded-md max-w-lg w-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-2xl gold-text">{POLICIES[policy].title}</h3>
                <button onClick={() => setPolicy(null)} className="text-gold" aria-label="Close" data-testid="policy-modal-close">
                  <X size={22} />
                </button>
              </div>
              <p className="text-sm text-muted leading-relaxed">{POLICIES[policy].body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
}
